import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { SiteHeader } from '@/components/site-header'
import { SiteFooter } from '@/components/site-footer'
import { DocumentList } from './document-list'
import { EmptyState } from './empty-state'
import { Pagination } from '@/components/ui/pagination'
import { PaginationControls } from '@/components/ui/pagination-controls'
import {
  useCreateDocument,
  useDeleteDocument,
  useDocumentList,
} from '@/lib/documents-query'
import { useToast } from '@/hooks/use-toast'

export function Dashboard() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [page, setPage] = useState(1)
  const [createOpen, setCreateOpen] = useState(false)
  const [newTitle, setNewTitle] = useState('')

  const list = useDocumentList(page)
  const create = useCreateDocument()
  const remove = useDeleteDocument()

  const handleCreate = async () => {
    try {
      const doc = await create.mutateAsync()
      toast({ title: 'Document created' })
      setCreateOpen(false)
      setNewTitle('')
      navigate({ to: '/d/$id', params: { id: doc.id } })
    } catch {
      toast({ title: 'Error', description: 'Failed to create document.', variant: 'destructive' })
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await remove.mutateAsync(id)
      toast({ title: 'Document deleted' })
    } catch {
      toast({ title: 'Error', description: 'Failed to delete document.', variant: 'destructive' })
    }
  }

  const docs = list.data?.data ?? []
  const totalPages = list.data?.meta.last_page ?? 1

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1 overflow-y-auto py-6">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-semibold">My Documents</h1>
            <Button onClick={handleCreate} disabled={create.isPending}>
              {create.isPending ? 'Creating...' : 'New document'}
            </Button>
          </div>

          <div className="mt-6">
            {list.isPending ? (
              <div className="flex h-32 items-center justify-center text-muted-foreground">
                Loading documents...
              </div>
            ) : docs.length === 0 ? (
              <EmptyState
                title="No documents yet"
                description="Create your first document to get started."
                action={
                  <Button onClick={handleCreate} disabled={create.isPending}>
                    {create.isPending ? 'Creating...' : 'Create document'}
                  </Button>
                }
              />
            ) : (
              <>
                <DocumentList
                  documents={docs}
                  onEdit={(id) => navigate({ to: '/d/$id', params: { id } })}
                  onDelete={handleDelete}
                />
                <div className="mt-6">
                  <Pagination>
                    <PaginationControls
                      currentPage={page}
                      totalPages={totalPages}
                      onPageChange={setPage}
                    />
                  </Pagination>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
      <SiteFooter />

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create new document</DialogTitle>
            <DialogDescription>
              Enter a title for your new document. You can change it later.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <Input
              placeholder="Untitled document"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              disabled={create.isPending}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={create.isPending}>
              {create.isPending ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
